/**
 * Backup Operations Help Content
 *
 * Feature: 136-dashboard-alignment (T051) · rewritten by 214-backup-ui-consistency (T039/FR-027)
 * Pages: /backup and its tabs (Dashboard / Plans / Management / Schedules / Restore / Backups /
 * Health / Recoverability / Reports / Settings) + /backup/retention.
 *
 * Spec 214: this content describes the SHIPPED Spec-201 consolidated Backup section — the old
 * text still documented raw CRON authoring and pages ("Backup Runs") that were removed at the
 * Spec-201 cutover.
 */

import type { PageHelpContent } from '../../components/ui/PageHelpPanel';

export const backupOperationsHelp: PageHelpContent = {
  pageTitle: 'Backup Management',
  purpose:
    'One command center for protection: plans bind entities to a schedule, destination and retention; the Dashboard shows whether everything is protected and provably recoverable.',
  sections: [
    {
      title: 'Backup Lifecycle',
      paragraphs: [
        'Runs flow Pending → Running → Completed / Partial / Failed (or Cancelled). The success rate everywhere is strict: Completed ÷ finished runs — a Partial run (some tables failed) is visible as its own amber series and does not count as success, though its captured tables may still be restorable.',
      ],
      concepts: [
        { term: 'Pending / Running', definition: 'Queued or actively copying. In-flight runs are never in the success-rate denominator.' },
        { term: 'Completed', definition: 'Backup finished fully. Creates a restorable recovery point (with its manifest).' },
        { term: 'Partial', definition: 'Some tables failed. Not counted as success; captured tables can still be restorable.' },
        { term: 'Failed / Cancelled', definition: 'No usable result from this run. Failed latest runs surface on the Health tab with a reason.' },
      ],
    },
    {
      title: 'Plans, Schedules & Retention',
      paragraphs: [
        'A Backup Plan bundles one schedule + one destination (+ optionally a fallback retention policy). An entity belongs to exactly one plan; assigning it elsewhere moves it and its cadence switches immediately. Schedules are built VISUALLY (e.g. weekly full + daily incremental) — no cron authoring; the builder compiles them for the executor and previews the next runs.',
        'Retention resolves source policy → plan policy → type default. Tier (RPO/RTO objective) and retention are set per source on the Backups tab; the building blocks live on the Settings tab.',
      ],
      concepts: [
        { term: 'Full backup', definition: 'Complete snapshot; anchors the restore chain.' },
        { term: 'Incremental backup', definition: 'Only changes since the last backup; restores replay the chain from the anchoring full.' },
        { term: 'Legal hold', definition: 'Admin-only. Suspends retention — held points are never pruned or purged until released.' },
      ],
    },
    {
      title: 'Restore & Recoverability',
      paragraphs: [
        'Restores are non-destructive: whole restores create a NEW item named <source>_restored_<timestamp>; selective (per-table) restores write renamed copies (<table>_restored_<period>) into an existing item for side-by-side compare. Use the Test button to prove a point recovers without keeping the copy, and Query-in-place mounts to inspect a lakehouse point without any restore.',
        'The Recoverability tab is proof, not estimate: automated verification restores points into throwaway targets and maintains a per-asset component score; secondary copies and vault copies (3-2-1-1-0) show their real staleness vs the source point.',
      ],
    },
    {
      title: 'How To',
      steps: [
        { step: 1, instruction: 'Backups tab: review each backup in place — destination, cadence, start time, tier, retention, last run — and use "Back up now" for an ad-hoc full backup (honors the concurrency cap).' },
        { step: 2, instruction: 'Management tab: assign unprotected entities to a plan, or move entities between plans (bulk or per row).' },
        { step: 3, instruction: 'Health tab: every unprotected entity with a plain-language reason — retry, repoint its storage, or exclude it (managed out of coverage).' },
        { step: 4, instruction: 'Restore tab: pick a backup set, choose a recovery point on the timeline (not-restorable points show why), then Restore into a new item or Test-restore to prove it.' },
        { step: 5, instruction: 'Retention & recycle bin (from the Plans tab): soft-deleted points can be restored during their grace window; Admins can release holds or permanently purge (deletes the blobs).' },
      ],
    },
    {
      title: 'Related Pages',
      links: [
        { label: 'Backup Dashboard', href: '/backup/dashboard' },
        { label: 'Plans', href: '/backup/plans' },
        { label: 'Retention & recycle bin', href: '/backup/retention' },
        { label: 'Lakehouses', href: '/lakehouses' },
      ],
    },
  ],
};

export default backupOperationsHelp;
